import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Skeleton } from '@shared/ui';
import { formatAmount } from '@shared/lib/format-amount';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';

interface WithdrawalAmountFieldProps {
  amount: string;
  network: string;
  cap: number | null;
  error?: string;
  isPending: boolean;
  balanceError?: unknown;
  disabled?: boolean;
  onChange: (amount: string) => void;
  onMax: () => void;
  onRetryBalance?: () => void;
}

export function WithdrawalAmountField({
  amount,
  network,
  cap,
  error,
  isPending,
  balanceError,
  disabled,
  onChange,
  onMax,
  onRetryBalance,
}: WithdrawalAmountFieldProps) {
  const { t } = useTranslation('cabinet');

  const renderHint = () => {
    if (!network) {
      return <p className="text-xs text-muted-foreground">{t('withdrawal.modal.availableBalanceSelectNetwork')}</p>;
    }

    if (balanceError) {
      return (
        <div className="space-y-2">
          <p className="text-xs text-destructive">
            {getApiErrorMessage(balanceError, t, 'withdrawal.modal.balanceLoadError')}
          </p>
          {onRetryBalance ? (
            <Button type="button" variant="outline" size="sm" onClick={onRetryBalance}>
              {t('common.retry', { ns: 'common' })}
            </Button>
          ) : null}
        </div>
      );
    }

    if (isPending) return <Skeleton className="h-4 w-48" />;

    if (cap == null) {
      return <p className="text-xs text-destructive">{t('withdrawal.modal.availableUnknown')}</p>;
    }

    return (
      <p className="text-xs tabular-nums text-muted-foreground">
        {t('withdrawal.modal.availableBalance', { amount: formatAmount(cap) })}
      </p>
    );
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="withdrawal-amount">{t('withdrawal.modal.amount')}</Label>
      <div className="relative">
        <Input
          id="withdrawal-amount"
          value={amount}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0.00"
          className="pr-[7.5rem] tabular-nums"
          error={error}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 flex h-10 items-center justify-end gap-1 pr-1">
          <span className="text-xs font-medium text-muted-foreground">USDT</span>
          <Button
            type="button"
            variant="ghost"
            className="pointer-events-auto h-8 px-2 text-xs font-semibold"
            onClick={onMax}
            disabled={disabled || !network || cap == null || cap <= 0}
          >
            {t('withdrawal.modal.max')}
          </Button>
        </div>
      </div>
      {!error ? renderHint() : null}
    </div>
  );
}
